import type { ReactNode } from 'react';

/**
 * One icon per navigable route, keyed by path — shared between the
 * dashboard's `MODULE_TILES` grid and the app shell's sidebar, so a module
 * reads the same way in both places rather than getting a second icon
 * language for the same idea.
 *
 * Stroke-based, a 24px grid, one consistent weight — this is the app's first
 * icon set, so the convention starts here: `currentColor` throughout, sized
 * and coloured entirely by the caller, never a fixed fill.
 */
const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

export const MODULE_ICONS: Record<string, (props: { size?: number }) => ReactNode> = {
  '/people': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <circle cx="12" cy="8" r="3.2" />
      <path d="M5.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" />
    </svg>
  ),
  '/groups': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <circle cx="9" cy="8" r="2.6" />
      <circle cx="16.5" cy="9" r="2.2" />
      <path d="M3.5 19.5c0-3.2 2.4-5.3 5.5-5.3s5.5 2.1 5.5 5.3" />
      <path d="M15 14.8c2.6 .3 4.5 2.1 4.5 4.7" />
    </svg>
  ),
  '/events': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <rect x="4" y="5.5" width="16" height="14.5" rx="2.2" />
      <path d="M4 10h16" />
      <path d="M8 3.5v3.5M16 3.5v3.5" />
      <circle cx="12" cy="14.5" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  ),
  '/attendance': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <rect x="5.5" y="4.5" width="13" height="16" rx="2" />
      <path d="M9 4.5V4a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 4v.5" />
      <path d="M8.5 13.5l2 2 4-4.5" />
    </svg>
  ),
  '/calendar': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <rect x="4" y="5.5" width="16" height="14.5" rx="2.2" />
      <path d="M4 10h16" />
      <path d="M8 3.5v3.5M16 3.5v3.5" />
      <circle cx="9" cy="14" r=".9" fill="currentColor" stroke="none" />
      <circle cx="13.5" cy="14" r=".9" fill="currentColor" stroke="none" />
      <circle cx="9" cy="17" r=".9" fill="currentColor" stroke="none" />
    </svg>
  ),
  '/kids': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <rect x="4.5" y="4.5" width="15" height="15" rx="5" />
      <circle cx="9.3" cy="10.5" r=".9" fill="currentColor" stroke="none" />
      <circle cx="14.7" cy="10.5" r=".9" fill="currentColor" stroke="none" />
      <path d="M8.8 14.2c.9 1 2.5 1 3.2 1s2.3 0 3.2-1" />
    </svg>
  ),
  '/academic/schools': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <path d="M12 5 2.5 9.5 12 14l9.5-4.5L12 5Z" />
      <path d="M6 11.8v4.4c0 1.4 2.7 2.8 6 2.8s6-1.4 6-2.8v-4.4" />
      <path d="M21.5 9.5v5.5" />
    </svg>
  ),
  '/academic/my': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <circle cx="12" cy="8" r="3.2" />
      <path d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6" />
      <path d="M9 12.5 11 14.5 15.5 10" />
    </svg>
  ),
  '/academic/homologations': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M14 3v5h5" />
      <path d="M9 14.5 11 16.5 15.5 12" />
    </svg>
  ),
  '/forms': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <path d="M7 3.5h7l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-16a1 1 0 0 1 1-1z" />
      <path d="M14 3.5v4h4" />
      <path d="M8.5 12.5h7M8.5 15.5h7M8.5 18h4" />
    </svg>
  ),
  '/giving': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <rect x="4.5" y="9.5" width="15" height="10" rx="1.5" />
      <path d="M4.5 13.2h15" />
      <path d="M12 9.5v10" />
      <path d="M12 9.5c-1.2-2.8-2.8-5-4.6-4.6C5.8 5.2 6 7.3 8 8.3c1 .5 2.6 .9 4 1.2" />
      <path d="M12 9.5c1.2-2.8 2.8-5 4.6-4.6C18.2 5.2 18 7.3 16 8.3c-1 .5-2.6 .9-4 1.2" />
    </svg>
  ),
  '/finance': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <path d="M4.5 19.5h15" />
      <rect x="6.5" y="12.5" width="3" height="7" rx=".6" />
      <rect x="10.5" y="8.5" width="3" height="11" rx=".6" />
      <rect x="14.5" y="4.5" width="3" height="15" rx=".6" />
    </svg>
  ),
  '/billing': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <rect x="3.5" y="6" width="17" height="12" rx="2" />
      <path d="M3.5 10h17" />
      <path d="M7 14.5h4" />
    </svg>
  ),
  '/notifications': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <path d="M12 3.5c-2.3 0-4 1.9-4 4.2v3.3c0 .9-.3 1.8-.9 2.5l-1 1.2c-.5.6-.1 1.6.7 1.6h10.4c.8 0 1.2-1 .7-1.6l-1-1.2a3.9 3.9 0 0 1-.9-2.5V7.7c0-2.3-1.7-4.2-4-4.2z" />
      <path d="M10.2 18.5a1.9 1.9 0 0 0 3.6 0" />
    </svg>
  ),
  '/cloud': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <path d="M7 18.5a4.2 4.2 0 0 1-.6-8.36 5.3 5.3 0 0 1 10.28-1.86A4 4 0 0 1 17.5 18.5H7Z" />
    </svg>
  ),
  '/inventory': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <path d="M4 8.5 12 4l8 4.5-8 4.5-8-4.5Z" />
      <path d="M4 8.5v7L12 20l8-4.5v-7" />
      <path d="M12 13v7" />
    </svg>
  ),
  '/inventory/checkouts': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <path d="M4 8.5h13M17 8.5l-3-3M17 8.5l-3 3" />
      <path d="M20 15.5H7M7 15.5l3-3M7 15.5l3 3" />
    </svg>
  ),
  '/inventory/categories': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <path d="M13 4h5.5A1.5 1.5 0 0 1 20 5.5V11L11 20l-7-7L13 4Z" />
      <circle cx="16" cy="8" r="1.3" fill="currentColor" stroke="none" />
    </svg>
  ),
  '/inventory/locations': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <path d="M12 21S5 14.4 5 9.5a7 7 0 0 1 14 0C19 14.4 12 21 12 21Z" />
      <circle cx="12" cy="9.5" r="2.3" />
    </svg>
  ),
  '/inventory/suppliers': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <rect x="2.5" y="8" width="11" height="8" rx="1.2" />
      <path d="M13.5 11h3.6l3.4 3v2h-7z" />
      <circle cx="7" cy="18" r="1.6" />
      <circle cx="17" cy="18" r="1.6" />
    </svg>
  ),
  '/administration': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <path d="M4 7h9M17 7h3" />
      <circle cx="14" cy="7" r="2" />
      <path d="M4 12h3M11 12h9" />
      <circle cx="8" cy="12" r="2" />
      <path d="M4 17h9M17 17h3" />
      <circle cx="14" cy="17" r="2" />
    </svg>
  ),
  '/': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <path d="M4 11.5 12 4l8 7.5" />
      <path d="M6 10v9a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-9" />
      <path d="M10 20v-5.5a1.2 1.2 0 0 1 1.2-1.2h1.6a1.2 1.2 0 0 1 1.2 1.2V20" />
    </svg>
  ),
  '/communications': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <path d="M3.5 11 18 5.5a1 1 0 0 1 1.35 1.13l-2.1 11.2a1 1 0 0 1-1.5.65L11 15" />
      <path d="M3.5 11 11 15l-.4 4.3a.7.7 0 0 1-1.25.35L7 16.8" />
      <path d="M3.5 11l14-5.5" />
    </svg>
  ),
  '/finance/journal-entries': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <path d="M6 3.5h10.5a1.5 1.5 0 0 1 1.5 1.5v15.5l-3-1.8-3 1.8-3-1.8-3 1.8V6a2.5 2.5 0 0 1 2.5-2.5z" />
      <path d="M8.5 8h7M8.5 11.5h7M8.5 15h4" />
    </svg>
  ),
  '/platform/modules': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.3" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.3" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.3" />
      <path d="M15.5 17.3h5M18 14.8v5" />
    </svg>
  ),
  '/platform/smtp': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <rect x="3" y="5.5" width="13" height="10" rx="1.5" />
      <path d="M3 7l6.5 4.5L16 7" />
      <path d="M19 13.3v1.1M19 17.6v1.1M21.2 16h-1.1M17.6 16h-1.1M20.4 14.4l-.8.8M18.4 17.6l-.8.8M20.4 17.6l-.8-.8M18.4 14.4l-.8-.8" />
      <circle cx="19" cy="16" r="1.15" />
    </svg>
  ),
  '/platform/oauth-providers': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <circle cx="8.5" cy="15.5" r="3.2" />
      <path d="M10.7 13.3 18.5 5.5" />
      <path d="M15.5 8.5l2 2M18 6l2 2" />
    </svg>
  ),
  '/security': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <path d="M12 3.5 5 6v5.5c0 4.6 3 7.7 7 9 4-1.3 7-4.4 7-9V6z" />
      <path d="M9.3 12.2l1.9 1.9 3.5-3.9" />
    </svg>
  ),
  '/platform/denominations': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <path d="M12 3v3M10.4 4.5h3.2" />
      <path d="M12 6 19 10.5V20H5v-9.5z" />
      <path d="M9.5 20v-5a2.5 2.5 0 0 1 5 0v5" />
    </svg>
  ),
  '/platform/councils': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <circle cx="12" cy="5.5" r="2" />
      <circle cx="6" cy="17" r="2" />
      <circle cx="18" cy="17" r="2" />
      <path d="M12 7.5v2.7M10.2 11.8 7.5 15.3M13.8 11.8l2.7 3.5" />
    </svg>
  ),
  '/settings': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <circle cx="12" cy="12" r="2.8" />
      <path d="M12 4v2.4M12 17.6V20M4 12h2.4M17.6 12H20M6.6 6.6l1.7 1.7M15.7 15.7l1.7 1.7M17.4 6.6l-1.7 1.7M8.3 15.7l-1.7 1.7" />
    </svg>
  ),
  '/finance/reports': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <path d="M4 19.5h16" />
      <path d="M4.5 15.5l4.5-4.5 3 3 6-6.5" />
      <path d="M15 6.5h4.5V11" />
    </svg>
  ),
  '/administration/custom-fields': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <rect x="3.5" y="6" width="17" height="5" rx="1.3" />
      <path d="M7 8.5h6" />
      <rect x="3.5" y="14" width="17" height="5" rx="1.3" />
      <path d="M7 16.5h9" />
    </svg>
  ),
  '/administration/roles': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <rect x="3.5" y="5" width="17" height="14" rx="2" />
      <circle cx="9" cy="11" r="2" />
      <path d="M6.5 16c0-1.8 1.1-3 2.5-3s2.5 1.2 2.5 3" />
      <path d="M14 9.5h4M14 12.5h4" />
    </svg>
  ),
  '/administration/appearance': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <path d="M12 3.5a8.5 8.5 0 1 0 0 17c1 0 1.6-.6 1.6-1.4 0-.4-.2-.7-.4-1-.2-.3-.4-.6-.4-1 0-.8.6-1.4 1.4-1.4H16a4.5 4.5 0 0 0 4.5-4.5c0-4.2-3.8-7.7-8.5-7.7z" />
      <circle cx="7.5" cy="11" r="1" fill="currentColor" stroke="none" />
      <circle cx="9.5" cy="7.5" r="1" fill="currentColor" stroke="none" />
      <circle cx="14.5" cy="7.5" r="1" fill="currentColor" stroke="none" />
      <circle cx="16.5" cy="11" r="1" fill="currentColor" stroke="none" />
    </svg>
  ),
  '/platform/subscriptions': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <path d="M12 3.5 20.5 8 12 12.5 3.5 8z" />
      <path d="M3.5 12l8.5 4.5 8.5-4.5" />
      <path d="M3.5 16l8.5 4.5 8.5-4.5" />
    </svg>
  ),
  '/platform/storage': ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...STROKE}>
      <ellipse cx="12" cy="6" rx="7.5" ry="2.6" />
      <path d="M4.5 6v12c0 1.4 3.4 2.6 7.5 2.6s7.5-1.2 7.5-2.6V6" />
      <path d="M4.5 12c0 1.4 3.4 2.6 7.5 2.6s7.5-1.2 7.5-2.6" />
    </svg>
  ),
};
